<template>
  <div class="agent-recommend-card">
    <div class="agent-comment-card" :style="commentCardStyle">
      <div class="agent-comment-card__header">
        <div class="agent-comment-card__avatar-wrapper">
          <img class="agent-comment-card__avatar" :src="defaultAvatar" alt="" />
          <div class="agent-comment-card__avatar-ring"></div>
        </div>
        <div class="agent-comment-card__title">
          <p class="agent-comment-card__role">求人担当スタッフ</p>
          <p class="agent-comment-card__name">おすすめポイント</p>
        </div>
      </div>

      <div class="agent-comment-card__body">
        <p
          ref="commentRef"
          class="agent-comment-card__comment"
          :class="{ 'is-expanded': displayExpanded, 'is-desktop-clamped': showDesktopMore }"
          :style="commentStyle"
        >
          {{ displayedComment }}
          <span
            v-if="showDesktopMore"
            class="agent-comment-card__desktop-link-space"
            aria-hidden="true"
          >
            続きを読む
          </span>
        </p>
        <button
          v-if="isClamped && !isDesktop"
          type="button"
          class="agent-comment-card__more"
          @click="handleToggle"
        >
          {{ displayExpanded ? '閉じる' : '続きを読む' }}
        </button>
        <RouterLink
          v-if="showDesktopMore"
          class="agent-comment-card__more"
          :to="item.job.href ?? ''"
        >
          続きを読む
        </RouterLink>
      </div>
      <span class="agent-comment-card__pointer" aria-hidden="true"></span>
    </div>

    <div ref="jobCardRef" class="agent-recommend-card__job-card">
      <JobCard
        :company-name="item.job.companyName"
        :job-title="item.job.jobTitle"
        :prefecture-names="item.job.prefectureNames"
        :occupation-descriptions="item.job.occupationDescriptions"
        :industry-names="item.job.industryNames"
        :skills="item.job.skills"
        :wage-min="item.job.wageMin"
        :wage-max="item.job.wageMax"
        :image-url="item.job.imageUrl"
        :image-alt="item.job.imageAlt"
        :show-image-wrapper="item.job.showImageWrapper"
        :href="item.job.href"
        :show-new-chip="item.job.showNewChip"
        :outline-button-active="item.job.outlineButtonActive"
        :on-bookmark="item.job.onBookmark"
        :on-apply="item.job.onApply"
        :show-apply-button="true"
        action-type="detail-only"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import agentAvatar from '@/assets/images/platform-logo-agent.webp';
import JobCard from '@/components/shared/card/JobCard.vue';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { AgentRecommendationCardItem } from './types';

const props = defineProps<{
  item: AgentRecommendationCardItem;
}>();

const defaultAvatar = agentAvatar;
const commentRef = ref<HTMLElement | null>(null);
const jobCardRef = ref<HTMLElement | null>(null);
const internalExpanded = ref(false);
const viewportWidth = ref(typeof window === 'undefined' ? 1024 : window.innerWidth);
const measuredHeight = ref(0);
const collapsedHeight = 72; // 約3行分
const desktopBreakpoint = 640;

const jobCardHeight = ref(0);
const isDesktopClamped = ref(false);
const desktopDisplayedComment = ref(props.item.comment);
let measurementId = 0;

const isDesktop = computed(() => viewportWidth.value >= desktopBreakpoint);
const previewLength = computed(() => (isDesktop.value ? Number.POSITIVE_INFINITY : 80));
const resizeObserver = ref<ResizeObserver | null>(null);
const displayExpanded = computed(() => !isDesktop.value && internalExpanded.value);
const isMobileClamped = computed(
  () =>
    measuredHeight.value > collapsedHeight + 1 || props.item.comment.length > previewLength.value
);
const showDesktopMore = computed(() => isDesktopClamped.value && !!props.item.job.href);
const isClamped = computed(() =>
  isDesktop.value ? isDesktopClamped.value : isMobileClamped.value
);
const mobileTargetHeight = computed(() =>
  displayExpanded.value ? Math.max(measuredHeight.value, collapsedHeight) : collapsedHeight
);

const updateViewportWidth = () => {
  viewportWidth.value = typeof window === 'undefined' ? 1024 : window.innerWidth;
};

const measureJobCardHeight = () => {
  jobCardHeight.value = jobCardRef.value?.offsetHeight ?? 0;
};

const measureDesktopOverflow = () => {
  const currentMeasurementId = ++measurementId;
  desktopDisplayedComment.value = props.item.comment;
  isDesktopClamped.value = false;

  if (!commentRef.value || !isDesktop.value) {
    return;
  }

  nextTick(async () => {
    const el = commentRef.value;
    if (!el || currentMeasurementId !== measurementId) return;

    const isOverflowing = el.scrollHeight > el.clientHeight + 1;
    if (!isOverflowing) return;

    isDesktopClamped.value = true;
    await nextTick();

    const targetEl = commentRef.value;
    if (!targetEl || currentMeasurementId !== measurementId) return;

    let minLength = 0;
    let maxLength = props.item.comment.length;
    let fittedComment = '…';

    while (minLength <= maxLength) {
      const midLength = Math.floor((minLength + maxLength) / 2);
      const candidate = `${props.item.comment.slice(0, midLength)}…`;
      desktopDisplayedComment.value = candidate;
      await nextTick();
      if (currentMeasurementId !== measurementId) return;

      if (targetEl.scrollHeight <= targetEl.clientHeight + 1) {
        fittedComment = candidate;
        minLength = midLength + 1;
      } else {
        maxLength = midLength - 1;
      }
    }

    desktopDisplayedComment.value = fittedComment;
  });
};

const measureHeight = () => {
  if (!commentRef.value) return;
  const el = commentRef.value;

  const prevLineClamp = el.style.webkitLineClamp;
  const prevMaxHeight = el.style.maxHeight;
  el.style.webkitLineClamp = 'unset';
  el.style.maxHeight = 'none';
  measuredHeight.value = el.scrollHeight;
  el.style.webkitLineClamp = prevLineClamp;
  el.style.maxHeight = prevMaxHeight;
};

const updateMeasurements = () => {
  updateViewportWidth();
  measureJobCardHeight();

  nextTick(() => {
    if (isDesktop.value) {
      measureDesktopOverflow();
      return;
    }

    isDesktopClamped.value = false;
    desktopDisplayedComment.value = props.item.comment;
    measureHeight();
  });
};

onMounted(() => {
  updateMeasurements();
  window.addEventListener('resize', updateMeasurements);
  if (typeof ResizeObserver !== 'undefined' && jobCardRef.value) {
    resizeObserver.value = new ResizeObserver(() => {
      updateMeasurements();
    });
    resizeObserver.value.observe(jobCardRef.value);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateMeasurements);
  if (resizeObserver.value && jobCardRef.value) resizeObserver.value.unobserve(jobCardRef.value);
  resizeObserver.value?.disconnect();
});

watch(
  () => props.item.comment,
  () => {
    internalExpanded.value = false;
    updateMeasurements();
  }
);

const handleToggle = () => {
  if (isDesktop.value) return;
  internalExpanded.value = !internalExpanded.value;
  updateMeasurements();
};

const displayedComment = computed(() => {
  if (isDesktop.value) return desktopDisplayedComment.value;
  if (displayExpanded.value || !isClamped.value) return props.item.comment;
  return `${props.item.comment.slice(0, previewLength.value)}…`;
});

const commentCardStyle = computed(() =>
  isDesktop.value && jobCardHeight.value > 0 ? { height: `${jobCardHeight.value}px` } : {}
);
const commentStyle = computed(() => ({
  '--comment-max-height': `${mobileTargetHeight.value}px`,
}));
</script>

<style scoped lang="scss">
.agent-recommend-card {
  display: flex;
  width: 100%;
  min-width: 0;
  gap: 16px;
  justify-content: center;
  align-items: flex-start;
}

.agent-recommend-card__job-card {
  flex: 0 0 316px;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  order: 1;
}

.agent-recommend-card__job-card :deep(.job-card) {
  max-width: 100%;
  min-width: 0;
}

.agent-recommend-card__job-card :deep(.job-card__container) {
  min-width: 0;
}

.agent-comment-card {
  position: relative;
  flex: 0 0 235px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  gap: 16px;
  padding: 24px 16px;
  border-radius: 12px;
  background-color: $primary-dark;
  color: #fff;
  box-shadow: 0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -2px rgba(0, 0, 0, 0.1);
  order: 2;
}

.agent-comment-card__pointer {
  position: absolute;
  left: -10px;
  top: 44px;
  width: 0;
  height: 0;
  border-top: 9px solid transparent;
  border-bottom: 9px solid transparent;
  border-right: 10px solid $primary-dark;
}

.agent-comment-card__header {
  display: flex;
  align-items: center;
  gap: 16px;
}

.agent-comment-card__avatar-wrapper {
  position: relative;
  width: 56px;
  height: 56px;
}

.agent-comment-card__avatar {
  width: 56px;
  height: 56px;
  border-radius: 999px;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
}

.agent-comment-card__avatar-ring {
  position: absolute;
  top: 0;
  left: 0;
  width: 56px;
  height: 56px;
  border-radius: 999px;
  border: 1px solid #fff;
  box-sizing: border-box;
}

.agent-comment-card__title {
  display: flex;
  flex-direction: column;
  gap: 2px;
  letter-spacing: 0.55px;
}

.agent-comment-card__role {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
}

.agent-comment-card__name {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
}

.agent-comment-card__body {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  gap: 8px;
  align-items: flex-start;
  min-height: 0;
}

.agent-comment-card__comment {
  margin: 0;
  width: 100%;
  font-size: 12px;
  line-height: 1.6;
  letter-spacing: 0.55px;
  white-space: pre-wrap;
  overflow: hidden;
  max-height: var(--comment-max-height, 72px);
  transition: max-height 0.25s ease;
  will-change: max-height;
  min-height: 0;
}

.agent-comment-card__comment.is-expanded {
  overflow: visible;
}

.agent-comment-card__more {
  display: inline-flex;
  align-self: flex-end;
  border: none;
  background: transparent;
  color: #fff;
  font-size: 12px;
  letter-spacing: 0.55px;
  line-height: 1.4;
  padding: 0 0 1px;
  border-bottom: 1px solid #fff;
  cursor: pointer;
  text-decoration: none;
}

.agent-comment-card__desktop-link-space {
  display: none;
}

@media (max-width: 639px) {
  .agent-recommend-card {
    flex-direction: column;
    background-color: $primary-dark;
    padding: 16px;
    border-radius: 12px;
    gap: 16px;
    max-width: 332px;
    margin: 0 auto;
    align-items: center;
    overflow: hidden;
  }

  .agent-recommend-card__job-card,
  .agent-comment-card {
    flex: 1 1 auto;
    width: 100%;
    max-width: 100%;
  }

  .agent-comment-card__pointer {
    display: none;
  }

  .agent-comment-card {
    background-color: transparent;
    box-shadow: none;
    padding: 0;
    gap: 12px;
  }

  .agent-comment-card__more {
    display: inline-flex;
    margin-top: 4px;
  }

  .agent-comment-card {
    order: 1;
  }

  .agent-recommend-card__job-card {
    order: 2;
    display: flex;
    justify-content: center;
    width: 100%;
    max-width: 332px;
    align-items: flex-end;
  }

  .agent-comment-card__comment {
    line-clamp: 3;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    max-height: 72px;
  }

  .agent-comment-card__comment.is-expanded {
    line-clamp: unset;
    -webkit-line-clamp: unset;
    max-height: none;
  }
}

@media (min-width: 640px) {
  .agent-comment-card__comment {
    flex: 1 1 auto;
    max-height: none;
  }

  .agent-comment-card__desktop-link-space {
    display: inline-block;
    visibility: hidden;
    white-space: nowrap;
    margin-left: 8px;
  }

  .agent-comment-card__more {
    position: absolute;
    right: 0;
    bottom: 0;
    background-color: $primary-dark;
    line-height: 1.6;
  }
}
</style>
